import { useState, useRef } from "react";
import { bookingServices, type ServiceConfig } from "../../../mocks/bookingData";
import type { BookingItemData } from "../../../mocks/bookingData";
import type { BookingState } from "../page";
import { CALLOUT_MINIMUM } from "../page";
import ItemsFilterList from "./ItemsFilterList";

interface Step3ItemsProps {
  booking: BookingState;
  onUpdate: (updates: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
  services?: ServiceConfig[];
}

const LOAD_IMAGES: Record<string, string> = {
  mini: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20eight%20small%20tied%20black%20rubbish%20bin%20bags%20sitting%20neatly%20on%20a%20clean%20residential%20driveway%20beside%20a%20white%20van%2C%20bright%20natural%20daylight%2C%20top-down%20slight%20angle%2C%20sharp%20focus%2C%20simple%20uncluttered%20background%2C%20no%20text%20no%20people&width=400&height=260&seq=load-mini-real-v2&orientation=landscape",
  small: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20sixteen%20tied%20black%20bin%20bags%20and%20two%20washing%20machines%20stacked%20neatly%20in%20the%20open%20rear%20of%20a%20white%20removal%20van%20on%20a%20sunny%20residential%20street%2C%20bright%20daylight%2C%20sharp%20focus%2C%20wide%20angle%2C%20no%20text%20no%20people&width=400&height=260&seq=load-small-real-v2&orientation=landscape",
  medium: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20medium%20household%20clearance%20load%20inside%20a%20large%20white%20van%20showing%20a%20large%20corner%20sofa%20several%20black%20bin%20bags%20and%20cardboard%20boxes%20neatly%20loaded%2C%20bright%20interior%20van%20lighting%2C%20sharp%20focus%2C%20wide%20angle%20rear%20view%2C%20no%20text%20no%20people&width=400&height=260&seq=load-medium-real-v2&orientation=landscape",
  large: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20large%20clearance%20load%20in%20the%20open%20rear%20of%20a%20white%20van%20showing%20a%20corner%20sofa%20two%20armchairs%20a%20tall%20wooden%20wardrobe%20a%20flatscreen%20TV%20several%20cardboard%20boxes%20and%20black%20bin%20bags%20tightly%20packed%2C%20bright%20daylight%2C%20sharp%20focus%2C%20wide%20rear%20angle%2C%20no%20text%20no%20people&width=400&height=260&seq=load-large-real-v2&orientation=landscape",
  xlarge: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20very%20large%20household%20clearance%20collection%20on%20a%20driveway%20showing%20a%20double%20bed%20frame%20two%20grey%20sofas%20a%20white%20fridge%20freezer%20a%20washing%20machine%20a%20tall%20wardrobe%20numerous%20cardboard%20boxes%20and%20many%20black%20bin%20bags%20stacked%20together%20equivalent%20to%20half%20a%20Luton%20van%2C%20bright%20sunny%20day%2C%20wide%20angle%2C%20sharp%20focus%2C%20no%20text%20no%20people&width=400&height=260&seq=load-xlarge-real-v2&orientation=landscape",
  xxlarge: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20maximum%20capacity%20full%20house%20clearance%20with%20120%20bin%20bags%20bedroom%20furniture%20living%20room%20sofas%20kitchen%20appliances%20wardrobes%20a%20fridge%20washing%20machine%20and%20dozens%20of%20cardboard%20boxes%20and%20bin%20bags%20all%20piled%20high%20on%20a%20residential%20driveway%2C%20bright%20sunny%20day%2C%20wide%20angle%2C%20sharp%20focus%2C%20no%20text%20no%20people&width=400&height=260&seq=load-xxlarge-real-v2&orientation=landscape",
  monster: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20an%20enormous%20full%20house%20clearance%20pile%20on%20a%20large%20residential%20driveway%20with%20over%20120%20black%20bin%20bags%20multiple%20sofas%20wardrobes%20beds%20white%20goods%20appliances%20and%20dozens%20of%20cardboard%20boxes%20stacked%20very%20high%20ready%20for%20a%20large%20removal%20van%2C%20bright%20sunny%20day%2C%20wide%20angle%2C%20sharp%20focus%2C%20no%20text%20no%20people&width=400&height=260&seq=load-monster-real-v1&orientation=landscape",
  luton: "https://cleario.uk/wp-content/uploads/2026/04/edited_1db04ab2602ef8e02d61cd54fd5386d3_5457d3f5.jpg",
};

const PAYG_BENEFITS = [
  "Only pay for what we collect",
  "No call out fee on returns up to an XX Large",
  "Only pay minimum charge today (includes a mini load)",
  "Option to add extra units now or pay on the day",
];

export default function Step3Items({ booking, onUpdate, onNext, onBack, services = bookingServices }: Step3ItemsProps) {
  const [error, setError] = useState("");
  const [photos, setPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const serviceConfig = services.find((s) => s.slug === booking.serviceSlug);
  if (!serviceConfig) return null;

  const { stepType, options, itemsConfig } = serviceConfig;
  const isLoadBased = stepType === "load";

  const handleOptionSelect = (id: string, label: string, price: number) => {
    onUpdate({ selectedOption: id, selectedOptionLabel: label, selectedOptionPrice: price });
    setError("");
  };

  const updateItemQty = (item: BookingItemData, delta: number) => {
    const existing = booking.cartItems.find((c) => c.id === item.id);
    if (!existing && delta > 0) {
      onUpdate({ cartItems: [...booking.cartItems, { id: item.id, label: item.label, quantity: 1, unitPrice: item.unitPrice }] });
    } else if (existing) {
      const newQty = existing.quantity + delta;
      if (newQty <= 0) {
        onUpdate({ cartItems: booking.cartItems.filter((c) => c.id !== item.id) });
      } else {
        onUpdate({ cartItems: booking.cartItems.map((c) => c.id === item.id ? { ...c, quantity: newQty } : c) });
      }
    }
    setError("");
  };

  const cartTotal = booking.cartItems.reduce((s, c) => s + c.quantity * c.unitPrice, 0);

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const newPhotos = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, 8 - photos.length)
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const flexibleOption = options.find((o) => o.id === "flexible");
  const fixedOptions = options.filter((o) => o.id !== "flexible");

  const handleContinue = () => {
    if (stepType === "items" && booking.cartItems.length === 0) {
      setError("Please add at least one item to continue");
      return;
    }
    if (stepType !== "items" && !booking.selectedOption) {
      setError("Please select a load size to continue");
      return;
    }
    setError("");
    onNext();
  };

  return (
    <div className="w-full">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-bold text-cl-dark/50 hover:text-cl-dark transition-colors cursor-pointer mb-6">
        <i className="ri-arrow-left-line"></i>
        Back
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-black text-cl-dark">Select Product</h1>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((s) => (
                <i key={s} className={`text-sm ${s <= 4 ? "ri-star-fill text-amber-400" : "ri-star-half-fill text-amber-400"}`}></i>
              ))}
            </div>
            <span className="text-xs font-bold text-cl-dark">4.8 out of 5</span>
            <span className="text-xs text-gray-400 font-semibold">based on 200+ reviews</span>
          </div>
          <span className="text-xs text-gray-400 font-semibold hidden sm:inline">Waste carrier&apos;s licence: CBDU 634440</span>
        </div>
      </div>

      <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-6 thick-border cartoon-shadow-sm">
        <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Step 2 of 3</span>
      </div>

      {isLoadBased && (
        <>
          {flexibleOption && (
            <div className="mb-6">
              <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
                <div className="bg-cl-green px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-scales-3-line text-white text-lg"></i>
                    <span className="text-white font-black text-sm uppercase tracking-wider">Pay as you go</span>
                  </div>
                  <span className="text-white text-xs font-black px-3 py-1 rounded-full border border-white/40">Flexible</span>
                </div>

                <div className="p-7 flex flex-col sm:flex-row gap-8">
                  <div className="flex-1">
                    <ul className="flex flex-col gap-2.5 mb-5">
                      {PAYG_BENEFITS.map((b) => (
                        <li key={b} className="flex items-start gap-2.5">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-cl-green flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-white text-xs font-black"></i>
                          </span>
                          <span className="text-sm font-semibold text-cl-dark">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="sm:w-72 flex-shrink-0">
                    <button
                      onClick={() => handleOptionSelect(flexibleOption.id, flexibleOption.label, flexibleOption.basePrice)}
                      className={`w-full rounded-2xl border-2 p-5 text-left transition-all cursor-pointer ${
                        booking.selectedOption === flexibleOption.id
                          ? "border-cl-green bg-[#f0fce4] cartoon-shadow-green"
                          : "border-transparent hover:border-cl-green hover:-translate-y-0.5"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-black text-cl-dark uppercase tracking-wide">{flexibleOption.label}</span>
                        {booking.selectedOption === flexibleOption.id && (
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-cl-green">
                            <i className="ri-check-line text-white text-xs"></i>
                          </span>
                        )}
                      </div>
                      <div className="flex items-end gap-1.5 mb-1">
                        <p className="text-2xl font-black text-cl-dark leading-none">{flexibleOption.priceLabel}</p>
                        <span className="text-sm font-bold text-gray-500 mb-0.5">inc VAT</span>
                      </div>
                      <p className="text-xs text-gray-500 font-semibold leading-relaxed mt-2">{flexibleOption.details}</p>
                      {(flexibleOption as typeof flexibleOption & { maxLabour?: string; maxVolume?: string; maxWeight?: string }).maxVolume && (
                        <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 gap-1">
                          <div className="flex items-center gap-1.5"><i className="ri-time-line text-cl-green text-xs"></i><span className="text-[10px] text-gray-500 font-semibold">Max Labour: {(flexibleOption as typeof flexibleOption & { maxLabour?: string }).maxLabour}</span></div>
                          <div className="flex items-center gap-1.5"><i className="ri-box-3-line text-cl-green text-xs"></i><span className="text-[10px] text-gray-500 font-semibold">Max Volume: {(flexibleOption as typeof flexibleOption & { maxVolume?: string }).maxVolume}</span></div>
                          <div className="flex items-center gap-1.5"><i className="ri-scales-3-line text-cl-green text-xs"></i><span className="text-[10px] text-gray-500 font-semibold">Max Weight: {(flexibleOption as typeof flexibleOption & { maxWeight?: string }).maxWeight}</span></div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-black text-cl-dark text-lg">Fixed loads</h2>
              <span className="text-xs text-gray-400 font-semibold">Select your size</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {fixedOptions.map((opt) => {
                const selected = booking.selectedOption === opt.id;
                const extOpt = opt as typeof opt & { wasPrice?: string; similarTo?: string; maxLabour?: string; maxVolume?: string; maxWeight?: string };
                return (
                  <button key={opt.id} onClick={() => handleOptionSelect(opt.id, opt.label, opt.basePrice)}
                    className={`relative bg-white rounded-2xl overflow-hidden text-left transition-all duration-200 cursor-pointer border-2 hover:-translate-y-1 flex flex-col ${selected ? "border-cl-green ring-2 ring-cl-green/30" : "border-transparent hover:border-cl-green"}`}>
                    {/* Image */}
                    <div className="w-full h-40 bg-white overflow-hidden flex-shrink-0">
                      <img src={LOAD_IMAGES[opt.id] ?? ""} alt={opt.label} className="w-full h-full object-cover object-top" />
                    </div>

                    {/* Badges */}
                    {opt.popular && (
                      <span className="absolute top-3 right-3 bg-cl-green text-white text-xs font-black px-2.5 py-1 rounded-full border border-[#4a8c0a] leading-tight">Popular</span>
                    )}
                    {selected && (
                      <span className="absolute top-3 left-3 w-7 h-7 flex items-center justify-center rounded-full bg-cl-green border-2 border-[#4a8c0a]">
                        <i className="ri-check-line text-white text-sm font-black"></i>
                      </span>
                    )}

                    <div className="p-4 flex flex-col flex-1">
                      {/* Title */}
                      <p className="text-sm font-black text-cl-dark uppercase tracking-wide leading-tight mb-2">{opt.label}</p>

                      {/* Price row */}
                      <div className="flex items-end gap-1.5 mb-0.5">
                        <p className="text-xl font-black text-cl-dark leading-none">{opt.priceLabel}</p>
                        {extOpt.wasPrice && (
                          <p className="text-xs text-gray-400 font-semibold line-through mb-0.5">{extOpt.wasPrice}</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 font-semibold mb-3">inc. VAT</p>

                      {/* Similar in size to */}
                      {extOpt.similarTo && (
                        <div className="mb-3">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Similar in size to</p>
                          <p className="text-xs text-cl-dark font-semibold leading-snug">{extOpt.similarTo}</p>
                        </div>
                      )}

                      {/* Specs grid */}
                      {(extOpt.maxLabour || extOpt.maxVolume || extOpt.maxWeight) && (
                        <div className="mt-auto pt-3 border-t border-gray-100 grid grid-cols-1 gap-1">
                          {extOpt.maxLabour && (
                            <div className="flex items-center gap-1.5">
                              <i className="ri-time-line text-cl-green text-xs flex-shrink-0"></i>
                              <span className="text-[10px] text-gray-500 font-semibold">Max Labour: {extOpt.maxLabour}</span>
                            </div>
                          )}
                          {extOpt.maxVolume && (
                            <div className="flex items-center gap-1.5">
                              <i className="ri-box-3-line text-cl-green text-xs flex-shrink-0"></i>
                              <span className="text-[10px] text-gray-500 font-semibold">Max Volume: {extOpt.maxVolume}</span>
                            </div>
                          )}
                          {extOpt.maxWeight && (
                            <div className="flex items-center gap-1.5">
                              <i className="ri-scales-3-line text-cl-green text-xs flex-shrink-0"></i>
                              <span className="text-[10px] text-gray-500 font-semibold">Max Weight: {extOpt.maxWeight}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {stepType === "items" && itemsConfig && (
        <>
          {/* Callout charge banner */}
          {serviceConfig?.calloutCharge && serviceConfig.calloutCharge > 0 && cartTotal < CALLOUT_MINIMUM && (
            <div className="mb-5 bg-cl-lime border-2 border-cl-green rounded-2xl px-5 py-4 flex items-start gap-3"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-cl-green text-white flex-shrink-0"
              >
                <i className="ri-truck-line text-lg"></i>
              </span>
              <div>
                <p className="text-sm font-black text-cl-dark mb-0.5"
                >Price + £{serviceConfig.calloutCharge} single callout charge</p>
                <p className="text-xs text-cl-dark/70 font-semibold leading-relaxed"
                >A one-time £{serviceConfig.calloutCharge} callout fee applies. Add £{CALLOUT_MINIMUM - cartTotal > 0 ? (CALLOUT_MINIMUM - cartTotal).toFixed(2) : "0"} more to waive it.</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex flex-wrap items-center gap-4">
              {booking.cartItems.length > 0 && (
                <div className="flex items-center gap-2 bg-cl-lime border-2 border-cl-green rounded-full px-4 py-1.5">
                  <i className="ri-shopping-cart-line text-cl-dark text-sm"></i>
                  <span className="text-xs font-black text-cl-dark">
                    {booking.cartItems.reduce((s, c) => s + c.quantity, 0)} item(s) &nbsp;·&nbsp; £{cartTotal.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mb-5">
            <h2 className="text-2xl font-black text-cl-dark">Filter your items</h2>
            <p className="text-sm text-cl-dark/60 font-semibold mt-1">Select all of your items before the next step.</p>
          </div>

          <div className="mb-5 bg-white rounded-2xl cartoon-card overflow-hidden">
            <div className="px-5 py-4 border-b-2 border-transparent flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green flex-shrink-0">
                <i className="ri-camera-line text-sm"></i>
              </span>
              <div>
                <h3 className="font-black text-cl-dark text-sm">Photos of your collection</h3>
                <p className="text-xs text-gray-500 font-semibold">Help prepare our teams prior to arrival — optional but recommended.</p>
              </div>
            </div>
            <div className="p-5">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); addPhotos(e.dataTransfer.files); }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl px-6 py-8 flex flex-col items-center gap-2 cursor-pointer transition-all ${dragging ? "border-cl-green bg-[#f0fce4]" : "border-transparent hover:border-cl-green hover:bg-[#f8fef4]"}`}
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-cl-lime border-2 border-cl-green text-cl-green">
                  <i className="ri-upload-cloud-2-line text-xl"></i>
                </span>
                <p className="text-sm font-black text-cl-dark">Click to upload or drag &amp; drop</p>
                <p className="text-xs text-gray-500 font-semibold">JPG, PNG, WEBP &nbsp;·&nbsp; up to 8 photos</p>
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} />
              </div>

              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {photos.map((p, idx) => (
                    <div key={idx} className="relative group">
                      <div className="w-full aspect-square rounded-lg overflow-hidden border-2 border-transparent">
                        <img src={p.preview} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                      </div>
                      <button onClick={() => removePhoto(idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 border-2 border-white text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                  {photos.length < 8 && (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="w-full aspect-square rounded-lg border-2 border-dashed border-transparent hover:border-cl-green flex items-center justify-center text-gray-400 hover:text-cl-green transition-colors cursor-pointer">
                      <i className="ri-add-line text-xl"></i>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <ItemsFilterList items={itemsConfig} cartItems={booking.cartItems} onQtyChange={updateItemQty} />

          {booking.cartItems.length > 0 && (
            <div className="mt-4 bg-cl-dark rounded-2xl px-6 py-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-white/70 text-xs font-semibold">{booking.cartItems.reduce((s, c) => s + c.quantity, 0)} item(s) selected</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {booking.cartItems.slice(0, 3).map((c) => (
                    <span key={c.id} className="text-xs text-cl-lime font-bold">{c.label}{c.quantity > 1 ? ` x${c.quantity}` : ""}</span>
                  ))}
                  {booking.cartItems.length > 3 && <span className="text-xs text-white/50 font-semibold">+{booking.cartItems.length - 3} more</span>}
                </div>
                {serviceConfig?.calloutCharge && serviceConfig.calloutCharge > 0 && cartTotal < CALLOUT_MINIMUM && (
                  <p className="text-xs text-white/50 font-semibold mt-1">+ £{serviceConfig.calloutCharge} callout fee</p>
                )}
                {serviceConfig?.calloutCharge && serviceConfig.calloutCharge > 0 && cartTotal >= CALLOUT_MINIMUM && (
                  <p className="text-xs text-cl-lime font-semibold mt-1">✓ Callout fee waived</p>
                )}
              </div>
              <span className="text-cl-lime font-black text-xl flex-shrink-0">
                £{(cartTotal + (cartTotal < CALLOUT_MINIMUM ? (serviceConfig?.calloutCharge ?? 0) : 0)).toFixed(2)}
              </span>
            </div>
          )}
        </>
      )}

      {stepType === "schedule" && (
        <>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-cl-dark mb-2">Choose your schedule</h2>
            <p className="text-cl-dark/60 font-semibold">Fixed prices, no surprises on the day.</p>
          </div>
          <div className="bg-white rounded-2xl cartoon-card overflow-hidden divide-y-2 divide-transparent">
            {options.map((opt) => {
              const selected = booking.selectedOption === opt.id;
              return (
                <button key={opt.id} onClick={() => handleOptionSelect(opt.id, opt.label, opt.basePrice)}
                  className={`w-full flex items-center justify-between gap-4 px-6 py-5 text-left transition-all cursor-pointer hover:bg-cl-tint/60 ${selected ? "bg-[#f0fce4]" : ""}`}>
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? "bg-cl-green border-cl-green" : "border-transparent"}`}>
                      {selected && <i className="ri-check-line text-white text-xs font-black"></i>}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-black text-cl-dark text-sm">{opt.label}</span>
                        {opt.popular && <span className="text-xs font-black text-white bg-cl-green px-2 py-0.5 rounded-full">Popular</span>}
                      </div>
                      <p className="text-xs text-gray-400 font-semibold mt-0.5">{opt.description}</p>
                      {opt.details && <p className="text-xs text-gray-400 mt-0.5">{opt.details}</p>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-black text-cl-dark text-base">{opt.priceLabel}</p>
                    <p className="text-xs text-gray-400">inc. VAT</p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {stepType === "rooms" && (
        <>
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-cl-dark mb-2">Select your property size</h2>
            <p className="text-cl-dark/60 font-semibold text-sm">All-inclusive fixed prices — everything cleared, nothing hidden.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {options.map((opt) => {
              const selected = booking.selectedOption === opt.id;
              const inclusions = opt.description.split(" · ");
              return (
                <button
                  key={opt.id}
                  onClick={() => handleOptionSelect(opt.id, opt.label, opt.basePrice)}
                  className={`relative bg-white rounded-2xl text-left transition-all duration-200 cursor-pointer border-2 hover:-translate-y-1 flex flex-col overflow-hidden ${
                    selected ? "border-cl-green ring-2 ring-cl-green/30" : "border-transparent hover:border-cl-green"
                  }`}
                  style={{ boxShadow: selected ? "0 4px 20px rgba(22,163,74,0.15)" : "0 2px 12px rgba(0,0,0,0.06)" }}
                >
                  {/* Header */}
                  <div className={`px-5 pt-5 pb-4 ${selected ? "bg-[#f0fce4]" : "bg-white"}`}>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-10 h-10 flex items-center justify-center rounded-xl border-2 flex-shrink-0 ${selected ? "bg-cl-green border-[#4a8c0a] text-white" : "bg-cl-lime border-cl-green text-cl-green"}`}>
                          <i className={`${opt.icon} text-base`}></i>
                        </span>
                        <div>
                          <p className="font-black text-cl-dark text-sm leading-tight">{opt.label}</p>
                          {opt.popular && (
                            <span className="inline-block text-[10px] font-black text-white bg-cl-green px-2 py-0.5 rounded-full mt-0.5">
                              ⭐ Most Popular
                            </span>
                          )}
                        </div>
                      </div>
                      {selected && (
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-cl-green flex-shrink-0">
                          <i className="ri-check-line text-white text-xs font-black"></i>
                        </span>
                      )}
                    </div>

                    {/* Price */}
                    <div className="flex items-end gap-2">
                      <span className="font-black text-cl-dark text-3xl leading-none">{opt.priceLabel}</span>
                      <span className="text-xs text-gray-400 font-semibold mb-0.5">inc. VAT</span>
                    </div>
                    {opt.details && (
                      <p className="text-xs text-cl-green font-bold mt-1">{opt.details}</p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100 mx-5"></div>

                  {/* What's included */}
                  <div className="px-5 py-4 flex-1">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5">What&apos;s included</p>
                    <ul className="flex flex-col gap-1.5">
                      {inclusions.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="w-4 h-4 flex items-center justify-center rounded-full bg-cl-lime border border-cl-green flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-cl-dark text-[9px] font-black"></i>
                          </span>
                          <span className="text-xs font-semibold text-cl-dark/80 leading-snug">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Info banner */}
          <div className="mt-6 bg-cl-tint border-2 border-cl-green/20 rounded-2xl px-5 py-4 flex items-start gap-3">
            <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-sm"></i>
            </span>
            <div>
              <p className="text-sm font-black text-cl-dark mb-0.5">Not sure which size fits?</p>
              <p className="text-xs text-cl-dark/60 font-semibold leading-relaxed">
                Choose the closest match — our team will assess on the day and adjust if needed. You can also call us on{" "}
                <a href="tel:03335775445" className="text-cl-green font-black">03335 775445</a> for a free survey.
              </p>
            </div>
          </div>
        </>
      )}

      <div className="mt-6 bg-white rounded-2xl p-5 cartoon-card flex items-center gap-4">
        <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green flex-shrink-0">
          <i className="ri-question-line text-base"></i>
        </span>
        <div className="flex-1">
          <p className="text-sm font-black text-cl-dark">Need help or have a problem?</p>
          <p className="text-xs text-gray-500 font-semibold">
            WhatsApp us or call us on{" "}
            <a href="tel:03335775445" className="text-cl-green font-black">03335 775445</a>.
          </p>
        </div>
        <a href="tel:03335775445"
          className="text-sm font-black text-cl-dark border-2 border-cl-dark rounded-xl px-4 py-2 hover:bg-cl-dark hover:text-white transition-all whitespace-nowrap">
          Contact us
        </a>
      </div>

      {error && (
        <div className="mt-4 flex items-center gap-2 text-sm font-bold text-red-500 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      <div className="flex items-center justify-end mt-8">
        <button onClick={handleContinue}
          className="bg-cl-green text-white font-black px-10 py-4 rounded-xl text-base cartoon-btn-green whitespace-nowrap cursor-pointer">
          Continue <i className="ri-arrow-right-line ml-1"></i>
        </button>
      </div>
    </div>
  );
}